// src/services/desvioEventosService.js
import { pb } from "./pocketbase";
import { isMonitored } from "../config/monitoredStatuses";
import { getActiveOperator } from "../utils/operatorUtils";

// Simple in-memory cache to speed up loading of reasons
const problemasCache = {};

export const desvioEventosService = {
  /**
   * Clears the memory cache for active problems.
   */
  clearCache() {
    delete problemasCache["VERIFICAR"];
    delete problemasCache["FALHA_BANCA"];
  },

  /**
   * Fetches active problems filtered by scope, including their categories.
   * @param {string} escopo - 'VERIFICAR' or 'FALHA_BANCA'
   */
  async getProblemas(escopo) {
    if (problemasCache[escopo]) {
      return problemasCache[escopo];
    }
    try {
      const records = await pb.collection("producao_problemas").getFullList({
        filter: `ativo = true && escopos ~ "${escopo}"`,
        expand: "categoria",
        sort: "categoria.nome,nome",
      });
      problemasCache[escopo] = records;
      return records;
    } catch (e) {
      console.error(`Erro ao buscar problemas para escopo ${escopo}:`, e);
      return [];
    }
  },

  /**
   * Creates a new problem inline.
   */
  async createProblema(nome, categoriaNome, escopo) {
    this.clearCache();
    try {
      const cleanCatName = categoriaNome.toUpperCase().trim();
      const cleanName = nome.toUpperCase().trim();

      // Find or create category
      let categoryId;
      try {
        const catRecord = await pb.collection("producao_problema_categorias").getFirstListItem(
          `nome = "${cleanCatName}"`
        );
        categoryId = catRecord.id;
      } catch (e) {
        const newCat = await pb.collection("producao_problema_categorias").create({
          nome: cleanCatName,
          ativo: true,
        });
        categoryId = newCat.id;
      }

      // Create problem (or check if it exists but inactive, reactivate it, or check if it already has this scope)
      let problemRecord;
      try {
        problemRecord = await pb.collection("producao_problemas").getFirstListItem(
          `nome = "${cleanName}" && categoria = "${categoryId}"`
        );
        
        // If it exists, make sure the scope is included
        const currentScopes = problemRecord.escopos || [];
        if (!currentScopes.includes(escopo) || !problemRecord.ativo) {
          const updatedScopes = Array.from(new Set([...currentScopes, escopo]));
          problemRecord = await pb.collection("producao_problemas").update(problemRecord.id, {
            escopos: updatedScopes,
            ativo: true,
          });
        }
      } catch (e) {
        // Doesn't exist, create new
        problemRecord = await pb.collection("producao_problemas").create({
          nome: cleanName,
          categoria: categoryId,
          escopos: [escopo],
          obs_obrigatoria: false,
          ativo: true,
        });
      }

      // Return fully expanded record
      const fullRecord = await pb.collection("producao_problemas").getOne(problemRecord.id, {
        expand: "categoria",
      });
      return fullRecord;
    } catch (e) {
      console.error("Erro ao criar problema inline:", e);
      throw e;
    }
  },

  /**
   * Creates a single deviation event.
   */
  async createEvento(eventoData) {
    const {
      status_destino,
      order_id,
      sku,
      order_number,
      status_anterior,
      problema_id,
      observacoes,
      registrado_por,
      preenchido,
      lote_evento,
    } = eventoData;

    try {
      // 1. Contar passagens reincidentes
      let qtd_passagens = 1;
      try {
        const previousEvents = await pb.collection("producao_desvio_eventos").getFullList({
          filter: `order_id = "${order_id}" && status_destino = "${status_destino}"`,
          fields: "id",
        });
        qtd_passagens = previousEvents.length + 1;
      } catch (e) {
        console.warn("Erro ao contar passagens, assumindo 1:", e);
      }

      // 2. Buscar logs de status anteriores para caminho_percorrido
      let caminho_percorrido = [];
      try {
        const logs = await pb.collection("system_logs").getFullList({
          filter: `metadata.itemId = "${order_id}"`,
          sort: "+created",
        });
        
        caminho_percorrido = logs
          .filter((l) => ["MOVER_STATUS", "MOVER_MASSA", "MOVER_STATUS_INDIVIDUAL"].includes(l.action))
          .map((l) => ({
            from: l.metadata?.oldStatus || "",
            to: l.metadata?.newStatus || "",
            user: l.user_name || "",
            date: l.created || "",
          }));
      } catch (e) {
        console.warn("Erro ao ler logs de caminhos, usando vazio:", e);
      }

      // 3. Resolver problema_id para 'NÃO INFORMADO' se não fornecido
      let resolvedProblemaId = problema_id || null;
      if (!resolvedProblemaId) {
        try {
          const recordNaoInformado = await pb.collection("producao_problemas").getFirstListItem(
            'nome = "NÃO INFORMADO"'
          );
          resolvedProblemaId = recordNaoInformado.id;
        } catch (err) {
          console.warn("Motivo 'NÃO INFORMADO' não encontrado para preenchimento padrão.");
        }
      }

      // 4. Criar o registro de evento de desvio
      const model = pb.authStore.model;
      const nameUpper = String(model?.name || "").toUpperCase();
      const userUpper = String(model?.username || "").toUpperCase();
      const termType = (nameUpper.includes("PDV") || userUpper.includes("PDV")) ? "PDV" : "OFICINA";
      const activeOp = getActiveOperator(termType);
      const record = await pb.collection("producao_desvio_eventos").create({
        status_destino,
        order_id,
        sku,
        order_number: order_number || "AVULSO",
        status_anterior: status_anterior || "N/I",
        problema: resolvedProblemaId,
        observacoes: observacoes || "",
        caminho_percorrido,
        qtd_passagens,
        lote_evento: lote_evento || "",
        registrado_por,
        registrado_por_operador: activeOp?.nome || "",
        preenchido: !!preenchido,
      });

      return record;
    } catch (e) {
      console.error("Erro ao registrar evento de desvio:", e);
      throw e;
    }
  },

  /**
   * Registers batch events under a common batch ID.
   */
  async createEventosLote(eventos, userId) {
    if (!eventos || eventos.length === 0) return [];
    
    const lote_evento = `lote_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    const promises = eventos.map((ev) => 
      this.createEvento({
        ...ev,
        registrado_por: userId,
        lote_evento,
      })
    );
    
    return Promise.all(promises);
  },

  /**
   * Increments transition counts in producao_fluxo_resumo.
   */
  async updateFluxoResumo(statusOrigem, statusDestino) {
    if (!statusOrigem || !statusDestino) return;

    try {
      const now = new Date();
      const ano = now.getFullYear();
      const mes = now.getMonth() + 1;

      let record;
      try {
        record = await pb.collection("producao_fluxo_resumo").getFirstListItem(
          `ano = ${ano} && mes = ${mes} && status_origem = "${statusOrigem}"`
        );
      } catch (e) {
        // Create new monthly summary block
        record = await pb.collection("producao_fluxo_resumo").create({
          ano,
          mes,
          status_origem: statusOrigem,
          total_saidas: 0,
          saidas_monitoradas: {},
        });
      }

      const total_saidas = (record.total_saidas || 0) + 1;
      const saidas_monitoradas = record.saidas_monitoradas || {};

      if (isMonitored(statusDestino)) {
        saidas_monitoradas[statusDestino] = (saidas_monitoradas[statusDestino] || 0) + 1;
      }

      await pb.collection("producao_fluxo_resumo").update(record.id, {
        total_saidas,
        saidas_monitoradas,
      });
    } catch (e) {
      console.error("Erro ao atualizar producao_fluxo_resumo:", e);
    }
  }
};
